/**
 * The folder tree (FR-15, FR-38, D16) — how the register **browses**: every
 * workspace the reader sees is a root, and its folders open beneath it **one
 * level at a time** (`GET /folders?workspace_id&parent_id`), so an
 * organisation with thousands of folders costs one small read per branch
 * opened, never the whole tree up front.
 *
 * Folders are records only — a rename or a move never touches a file (FR-10)
 * — and nest at most nine levels below the workspace (§9-16). A folder a
 * sibling app keeps (ProDesk's folder per client, say) sits in the same tree,
 * **grouped under its app** (*By app*): people file into it and browse it, and
 * its structure is that app's (§9-19, `APP_OWNED`), so it carries no rename,
 * move or archive here.
 *
 * W6 adds **Shared with me** (FR-29): what members' shares put in the
 * reader's sight, and as its children the folders shared with them
 * (`GET /folders?shared=true`) — each a leaf here, because a folder share is
 * recursive: choosing it lists everything under it.
 *
 * The breadcrumb (`GET /folders/{id}`) says where the register is standing —
 * the workspace, then each folder down — and every step is a way back up.
 *
 * W25 (FR-60, D76) puts the same tree **in the host's left menu**, under
 * Documents (`variant="menu"`, wired by `useProDocFolderMenu`): the menu's item
 * look, names cut short with the full name on hover, and the indent stopping
 * after four levels so a deep folder still fits the 240 px menu. There the
 * shell mounts the tree **twice** while its drawer is open — once in the drawer
 * and once in the rail the drawer covers — so the tree's state (the branches
 * read, the places open) is **the host's** (`useFolderTreeState`, passed as
 * `state`), and nothing in the tree carries a fixed `id`.
 *
 * Package rules (ADR-0010): the host's client, verbs and choices come in as
 * props; nothing here knows a router or a session.
 */
import { Alert, Button, DropdownMenu, Skeleton, SkeletonRegion, cn, type DropdownMenuItem } from '@fiveox/ui'
import { ChevronDown, ChevronRight, Ellipsis, Folder as FolderIcon, FolderOpen, Layers, Plus, Users } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { FOLDER, MAX_FOLDER_DEPTH, SHARE, isOwnFolder, type Folder, type FolderCrumb, type ProDocClient, type Verb, type Workspace } from './prodoc-client'
import { errorText, useLoad } from './hooks'

/** Where the register stands: a workspace (or all of them), and a folder in it (or its root). */
export interface FolderPlace {
  workspace: string | null
  folder: string | null
}

/** What a folder's menu asks the host's dialogs to do. */
export type FolderAction =
  | { kind: 'create'; workspaceId: string; parent: Folder | null }
  | { kind: 'rename'; folder: Folder }
  | { kind: 'move'; folder: Folder }
  | { kind: 'archive'; folder: Folder }
  /** W6: share it — live, with everything under it at read time (FR-29, §9-18). */
  | { kind: 'share'; folder: Folder }

/* ------------------------------------------------------------- the data */

/** One branch of the tree as read: a workspace's top level, or a folder's children. */
export interface FolderBranch {
  loading: boolean
  error: string
  folders: Folder[] | null
}
type Branch = FolderBranch

const branchKey = (workspaceId: string, parentId: string | null) => `${workspaceId}:${parentId ?? ''}`

/**
 * The tree's children, read lazily and kept: `open(workspace, parent)` reads a
 * branch the first time it is asked for, `refresh()` re-reads every branch
 * already read (after a create, a rename, a move or an archive).
 */
export function useFolderBranches(client: ProDocClient) {
  const [branches, setBranches] = useState<Record<string, Branch>>({})
  const asked = useRef(new Map<string, number>())

  const read = useCallback(
    async (workspaceId: string, parentId: string | null) => {
      const key = branchKey(workspaceId, parentId)
      const ticket = (asked.current.get(key) ?? 0) + 1
      asked.current.set(key, ticket)
      setBranches((b) => ({ ...b, [key]: { loading: true, error: '', folders: b[key]?.folders ?? null } }))
      try {
        const folders = await client.folders.list({ workspace_id: workspaceId, parent_id: parentId })
        if (asked.current.get(key) !== ticket) return
        setBranches((b) => ({ ...b, [key]: { loading: false, error: '', folders: folders.filter((f) => f.status !== 'archived') } }))
      } catch (e) {
        if (asked.current.get(key) !== ticket) return
        setBranches((b) => ({ ...b, [key]: { loading: false, error: errorText(e), folders: b[key]?.folders ?? null } }))
      }
    },
    [client],
  )

  // A new client is a new organisation or a new service: what was read is not theirs.
  useEffect(() => {
    asked.current.clear()
    setBranches({})
  }, [client])

  const open = useCallback(
    (workspaceId: string, parentId: string | null) => {
      if (!asked.current.has(branchKey(workspaceId, parentId))) void read(workspaceId, parentId)
    },
    [read],
  )

  const refresh = useCallback(() => {
    for (const key of asked.current.keys()) {
      const [workspaceId, parentId] = key.split(':') as [string, string]
      void read(workspaceId, parentId || null)
    }
  }, [read])

  const branch = useCallback((workspaceId: string, parentId: string | null): Branch | undefined => branches[branchKey(workspaceId, parentId)], [branches])

  /** A folder already read, in any branch — what a host walks up to reveal a place chosen elsewhere. */
  const find = useCallback(
    (folderId: string): Folder | undefined => {
      for (const b of Object.values(branches)) {
        const hit = b.folders?.find((f) => f.id === folderId)
        if (hit) return hit
      }
      return undefined
    },
    [branches],
  )

  return { branch, open, refresh, reload: read, find }
}

/**
 * The tree's whole state — the branches read and the places open — for a host
 * that mounts the tree more than once (W25: the shell's drawer and the rail it
 * covers) or unmounts it while its menu group is closed. Held by the host and
 * passed to each `<FolderTree state>`, it is **one** tree: a branch opened in
 * the drawer is open in the rail, and each branch is read once.
 */
export interface FolderTreeState {
  branch: (workspaceId: string, parentId: string | null) => FolderBranch | undefined
  /** Read a branch the first time it is asked for. */
  open: (workspaceId: string, parentId: string | null) => void
  /** Read a branch again, now. */
  reload: (workspaceId: string, parentId: string | null) => Promise<void>
  /** Re-read every branch already read — after a create, a rename, a move or an archive. */
  refresh: () => void
  /** A folder already read, in any branch. */
  find: (folderId: string) => Folder | undefined
  /** The workspaces and folders open, by id. */
  expanded: ReadonlySet<string>
  toggle: (key: string, onOpen?: () => void) => void
  /** Open these workspaces and folders (ids) — to reveal a place chosen elsewhere. */
  expand: (keys: string[]) => void
}

export interface FolderTreeStateOptions {
  /** Where the register stands — its workspace opens in the tree. `null`: the host is not on the register. */
  selected: FolderPlace | null
  /** The roots in sight; an open workspace's top level is read once it is among them. */
  workspaces: Workspace[]
  /** Bump to re-read every branch already read. */
  revision?: number
}

export function useFolderTreeState(client: ProDocClient, { selected, workspaces, revision = 0 }: FolderTreeStateOptions): FolderTreeState {
  const branches = useFolderBranches(client)
  const { branch, open, refresh, reload, find } = branches
  const selectedWorkspace = selected?.workspace ?? null
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(selectedWorkspace ? [selectedWorkspace] : []))

  useEffect(() => {
    if (revision > 0) refresh()
    // Only a new revision re-reads; `refresh` is stable per client.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision])

  // A workspace chosen elsewhere (the filter, a link) opens in the tree.
  useEffect(() => {
    if (selectedWorkspace) setExpanded((e) => (e.has(selectedWorkspace) ? e : new Set(e).add(selectedWorkspace)))
  }, [selectedWorkspace])

  useEffect(() => {
    for (const key of expanded) {
      const workspace = workspaces.find((w) => w.id === key)
      if (workspace) open(workspace.id, null)
    }
  }, [expanded, workspaces, open])

  // The latest set, for `toggle` to know whether it opens: the read it starts stays out of the state's updater.
  const current = useRef(expanded)
  current.current = expanded
  const toggle = useCallback((key: string, onOpen?: () => void) => {
    const opening = !current.current.has(key)
    setExpanded((e) => {
      const next = new Set(e)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
    if (opening) onOpen?.()
  }, [])

  const expand = useCallback((keys: string[]) => setExpanded((e) => (keys.every((k) => e.has(k)) ? e : new Set([...e, ...keys]))), [])

  return useMemo(() => ({ branch, open, reload, refresh, find, expanded, toggle, expand }), [branch, open, reload, refresh, find, expanded, toggle, expand])
}

/**
 * Siblings in the order a reader scans them (*By app*): this app's folders by
 * name first, then each other app's, its folders grouped under its name.
 */
export function groupByApp(folders: Folder[], app: string): { app: string | null; folders: Folder[] }[] {
  const byName = (a: Folder, b: Folder) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })
  const own = folders.filter((f) => isOwnFolder(f, app)).sort(byName)
  const others = new Map<string, Folder[]>()
  for (const f of folders) if (!isOwnFolder(f, app)) others.set(f.owner_app, [...(others.get(f.owner_app) ?? []), f])
  return [
    ...(own.length ? [{ app: null, folders: own }] : []),
    ...[...others.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([owner, list]) => ({ app: owner, folders: list.sort(byName) })),
  ]
}

/* ------------------------------------------------------------- the tree */

export interface FolderTreeProps {
  client: ProDocClient
  /** The roots: the workspaces the reader sees, from the host's list. */
  workspaces: Workspace[]
  workspacesLoading: boolean
  workspacesError: string
  onRetryWorkspaces: () => void
  /** The service's own slug (`/documents/options`'s `app`) — any other `owner_app` is a sibling's folder. */
  app: string
  /** Where the register stands, highlighted. `null`: the host is not on the register, so nothing is. */
  selected: FolderPlace | null
  onSelect: (place: FolderPlace) => void
  can: (resource: string, verb: Verb) => boolean
  /** A folder's menu chose something — the host opens its dialog. */
  onAction: (action: FolderAction) => void
  /** Bump to re-read every open branch (after a dialog changed something). With `state`, the host refreshes its state instead. */
  revision?: number
  /** W6: *Shared with me* — the host's read of the shared roots, and whether the register is standing in it. */
  shared?: SharedRoots
  /**
   * W25: the tree's state, held by the host (`useFolderTreeState`) — for a tree
   * mounted more than once, as a menu's panel is while the drawer is open.
   * Without it the tree keeps its own.
   */
  state?: FolderTreeState
  /**
   * W25 (FR-60): `panel` — the register's own left panel, as since W5; `menu` —
   * the host's left menu at 240 px: the menu's item look, and the indent
   * stopping after four levels.
   */
  variant?: 'panel' | 'menu'
  className?: string
}

/** How deep the menu's indent goes: past this a folder sits at the fourth level's indent (FR-60). */
export const MENU_INDENT_LEVELS = 4

type Look = 'panel' | 'menu'
const LookContext = createContext<Look>('panel')

/** A row's left padding: 14 px a level in the panel; in the menu 12 px past the item's edge, then 12 px a level up to four. */
function indentOf(look: Look, depth: number): number {
  return look === 'menu' ? 12 + Math.min(depth, MENU_INDENT_LEVELS) * 12 : depth * 14
}

/** What the tree shows under *Shared with me*. */
export interface SharedRoots {
  folders: Folder[] | null
  loading: boolean
  error: string
  /** The register shows the shared view. */
  active: boolean
  /** …inside this shared folder. */
  folder: string | null
  onSelect: (folder: Folder | null) => void
}

export function FolderTree(props: FolderTreeProps) {
  // The host's state, when it holds one; otherwise the tree's own — never both, so no branch is read twice.
  return props.state ? <TreeView {...props} state={props.state} /> : <OwnTree {...props} />
}

/** A tree that keeps its own state: the register's panel, and any host mounting it once. */
function OwnTree(props: FolderTreeProps) {
  const state = useFolderTreeState(props.client, { selected: props.selected, workspaces: props.workspaces, revision: props.revision })
  return <TreeView {...props} state={state} />
}

function TreeView(props: FolderTreeProps & { state: FolderTreeState }) {
  const { workspaces, workspacesLoading, workspacesError, onRetryWorkspaces, app, selected, onSelect, can, onAction, shared, className, state } = props
  const look: Look = props.variant ?? 'panel'
  const { expanded, toggle } = state
  const sharedActive = Boolean(shared?.active)
  // In the menu the states before the tree sit in from its edge, as its rows do.
  const edge = look === 'menu' ? 'px-3 py-1' : undefined

  if (workspacesLoading && workspaces.length === 0) {
    return (
      <SkeletonRegion label="Loading the folders" className={cn('space-y-2', edge, className)}>
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-6 w-36" />
      </SkeletonRegion>
    )
  }
  if (workspacesError && workspaces.length === 0) {
    return (
      <Alert tone="error" className={cn(look === 'menu' && 'mx-2 text-xs', className)}>
        Could not load the workspaces: {workspacesError}{' '}
        <Button variant="link" onClick={onRetryWorkspaces}>
          Try again
        </Button>
      </Alert>
    )
  }
  if (workspaces.length === 0) {
    return <p className={cn('text-muted-foreground', look === 'menu' ? 'text-xs' : 'text-sm', edge, className)}>No workspaces you can see yet — so no folders either.</p>
  }

  const ordered = [...workspaces].sort((a, b) => Number(Boolean(b.is_default)) - Number(Boolean(a.is_default)) || a.name.localeCompare(b.name))
  const everyActive = selected !== null && !selected.workspace && !sharedActive

  const tree = (
    <ul role="tree" aria-label="Workspaces and folders" className="space-y-0.5">
      <li role="treeitem" aria-selected={everyActive} className="list-none">
        <TreeRow depth={0} icon={<Layers size={15} aria-hidden />} label="Every workspace" active={everyActive} onSelect={() => onSelect({ workspace: null, folder: null })} />
      </li>
      {shared && <SharedBranch shared={shared} />}
      {ordered.map((w) => {
        const open = expanded.has(w.id)
        const root = state.branch(w.id, null)
        const here = selected?.workspace === w.id && !selected.folder && !sharedActive
        return (
          <li key={w.id} role="treeitem" aria-expanded={open} aria-selected={here} className="list-none">
            <TreeRow
              depth={0}
              expandable
              open={open}
              onToggle={() => toggle(w.id, () => state.open(w.id, null))}
              icon={<Layers size={15} aria-hidden />}
              label={w.is_default ? `${w.name} (default)` : w.name}
              active={here}
              onSelect={() => onSelect({ workspace: w.id, folder: null })}
              menu={
                can(FOLDER, 'can_create')
                  ? [{ id: 'create', label: 'New folder here…', icon: <Plus size={14} aria-hidden />, onSelect: () => onAction({ kind: 'create', workspaceId: w.id, parent: null }) }]
                  : []
              }
            />
            {open && <Children workspaceId={w.id} parent={null} branch={root} depth={1} {...{ app, selected, onSelect, can, onAction, state }} />}
          </li>
        )
      })}
    </ul>
  )

  return (
    <LookContext.Provider value={look}>
      {/* In a menu the tree sits inside the menu's own `nav`, so it is not a second landmark. */}
      {look === 'menu' ? (
        <div className={className}>{tree}</div>
      ) : (
        <nav aria-label="Folders" className={className}>
          {tree}
        </nav>
      )}
    </LookContext.Provider>
  )
}

/** *Shared with me*, and the folders shared with the reader beneath it — leaves, since a folder share reaches everything under it. */
function SharedBranch({ shared }: { shared: SharedRoots }) {
  const look = useContext(LookContext)
  const folders = (shared.folders ?? []).filter((f) => f.status !== 'archived')
  return (
    <li role="treeitem" aria-expanded={folders.length > 0 ? true : undefined} aria-selected={shared.active && !shared.folder} className="list-none">
      <TreeRow depth={0} icon={<Users size={15} aria-hidden />} label="Shared with me" active={shared.active && !shared.folder} onSelect={() => shared.onSelect(null)} />
      {shared.loading ? (
        <div style={{ paddingLeft: indentOf(look, 1) }}>
          <SkeletonRegion label="Loading the shared folders" className="py-1">
            <Skeleton className="h-5 w-32" />
          </SkeletonRegion>
        </div>
      ) : shared.error ? (
        <p className="text-muted-foreground py-1 text-xs" style={{ paddingLeft: indentOf(look, 1) + 22 }}>
          The shared folders could not be read.
        </p>
      ) : (
        folders.length > 0 && (
          <ul role="group" aria-label="Folders shared with me" className="space-y-0.5">
            {folders.map((f) => (
              <li key={f.id} role="treeitem" aria-selected={shared.folder === f.id} className="list-none">
                <TreeRow
                  depth={1}
                  icon={<FolderIcon size={15} aria-hidden />}
                  label={f.name}
                  active={shared.active && shared.folder === f.id}
                  onSelect={() => shared.onSelect(f)}
                  title={`${f.name} — shared with you, with everything filed under it`}
                />
              </li>
            ))}
          </ul>
        )
      )}
    </li>
  )
}

interface ChildrenProps {
  workspaceId: string
  parent: Folder | null
  branch: Branch | undefined
  depth: number
  app: string
  selected: FolderPlace | null
  onSelect: (place: FolderPlace) => void
  can: (resource: string, verb: Verb) => boolean
  onAction: (action: FolderAction) => void
  state: FolderTreeState
}

/** One branch's folders: loading, failed, empty, or the folders — this app's first, then each sibling app's under its name. */
function Children(props: ChildrenProps) {
  const { workspaceId, parent, branch, depth, app, selected, onSelect, can, onAction, state } = props
  const { expanded, toggle } = state
  const look = useContext(LookContext)
  const indent = (d: number) => indentOf(look, d)
  if (!branch || (branch.loading && !branch.folders)) {
    return (
      <div style={{ paddingLeft: indent(depth) }}>
        <SkeletonRegion label="Loading folders" className="py-1">
          <Skeleton className="h-5 w-32" />
        </SkeletonRegion>
      </div>
    )
  }
  if (branch.error && !branch.folders) {
    return (
      <p className="text-muted-foreground py-1 text-xs" style={{ paddingLeft: indent(depth) }}>
        Could not load these folders.{' '}
        <Button variant="link" size="sm" onClick={() => void state.reload(workspaceId, parent?.id ?? null)}>
          Try again
        </Button>
      </p>
    )
  }
  const folders = branch.folders ?? []
  if (folders.length === 0) {
    return (
      <p className="text-muted-foreground py-1 text-xs" style={{ paddingLeft: indent(depth) }}>
        {parent ? 'No folders inside.' : 'No folders yet.'}
        {can(FOLDER, 'can_create') && depth <= MAX_FOLDER_DEPTH && (!parent || isOwnFolder(parent, app)) && (
          <>
            {' '}
            <Button variant="link" size="sm" onClick={() => onAction({ kind: 'create', workspaceId, parent })}>
              New folder
            </Button>
          </>
        )}
      </p>
    )
  }
  return (
    <ul role="group" className="space-y-0.5">
      {groupByApp(folders, app).map((group) => (
        <li key={group.app ?? '__own'} className="list-none">
          {group.app && (
            <p className="text-muted-foreground pt-1 text-[11px] font-semibold tracking-wide uppercase" style={{ paddingLeft: indent(depth) + 22 }}>
              From {group.app}
            </p>
          )}
          <ul role="group" className="space-y-0.5">
            {group.folders.map((f) => {
              const open = expanded.has(f.id)
              const leaf = f.child_count === 0
              return (
                <li key={f.id} role="treeitem" aria-expanded={leaf ? undefined : open} aria-selected={selected?.folder === f.id} className="list-none">
                  <TreeRow
                    depth={depth}
                    expandable={!leaf}
                    open={open}
                    onToggle={() => toggle(f.id, () => state.open(workspaceId, f.id))}
                    icon={open ? <FolderOpen size={15} aria-hidden /> : <FolderIcon size={15} aria-hidden />}
                    label={f.name}
                    hint={typeof f.document_count === 'number' && f.document_count > 0 ? String(f.document_count) : undefined}
                    active={selected?.folder === f.id}
                    onSelect={() => onSelect({ workspace: workspaceId, folder: f.id })}
                    menu={folderMenu(f, app, can, onAction)}
                    title={isOwnFolder(f, app) ? f.name : `${f.name} — kept by ${f.owner_app}: file documents into it here; its name and place change in ${f.owner_app}.`}
                  />
                  {open && !leaf && (
                    <Children {...props} parent={f} branch={state.branch(workspaceId, f.id)} depth={depth + 1} />
                  )}
                </li>
              )
            })}
          </ul>
        </li>
      ))}
    </ul>
  )
}

/**
 * What a person may do to a folder: only this app's folders have their
 * structure changed here (§9-19); any folder in sight may be **shared** (W6) —
 * a share widens who sees it, and changes nothing about where it is.
 */
function folderMenu(f: Folder, app: string, can: (resource: string, verb: Verb) => boolean, onAction: (a: FolderAction) => void): DropdownMenuItem[] {
  const share: DropdownMenuItem[] = can(SHARE, 'can_create') ? [{ id: 'share', label: 'Share…', onSelect: () => onAction({ kind: 'share', folder: f }) }] : []
  if (!isOwnFolder(f, app)) return share
  const items: DropdownMenuItem[] = [...share]
  if (can(FOLDER, 'can_create') && f.depth < MAX_FOLDER_DEPTH) {
    items.push({ id: 'create', label: 'New folder inside…', onSelect: () => onAction({ kind: 'create', workspaceId: f.workspace_id, parent: f }) })
  }
  if (can(FOLDER, 'can_update')) {
    items.push({ id: 'rename', label: 'Rename…', onSelect: () => onAction({ kind: 'rename', folder: f }) })
    items.push({ id: 'move', label: 'Move…', onSelect: () => onAction({ kind: 'move', folder: f }) })
  }
  if (can(FOLDER, 'can_delete')) {
    items.push({ id: 'archive', label: 'Archive…', tone: 'danger', separatorBefore: items.length > 0, onSelect: () => onAction({ kind: 'archive', folder: f }) })
  }
  return items
}

/** One line of the tree: the opener, the name (selecting it), and its menu. In the menu, the shell's item look (`navItemClass`'s colours, `rounded-lg`, `text-sm`). */
function TreeRow({
  depth,
  expandable = false,
  open = false,
  onToggle,
  icon,
  label,
  hint,
  active,
  onSelect,
  menu = [],
  title,
}: {
  depth: number
  expandable?: boolean
  open?: boolean
  onToggle?: () => void
  icon: ReactNode
  label: string
  hint?: string
  active: boolean
  onSelect: () => void
  menu?: DropdownMenuItem[]
  /** The name in full, and anything a reader should know before choosing it. */
  title?: string
}) {
  const menuLook = useContext(LookContext) === 'menu'
  // The menu's rows are its items: the row carries the highlight and the hover, the controls in it stay flat.
  const flat = menuLook ? 'hover:bg-transparent' : undefined
  return (
    <div
      className={cn(
        'group flex min-w-0 items-center gap-0.5',
        menuLook ? cn('rounded-lg transition-colors', active ? 'bg-primary/10' : 'hover:bg-muted') : cn('rounded-md', active ? 'bg-primary/10' : 'hover:bg-muted/60'),
      )}
      style={{ paddingLeft: indentOf(menuLook ? 'menu' : 'panel', depth) }}
    >
      {expandable ? (
        <Button variant="ghost" size="sm" className={cn(menuLook ? 'h-8 w-5' : 'h-7 w-6', 'shrink-0 px-0', flat)} aria-label={`${open ? 'Close' : 'Open'} ${label}`} aria-expanded={open} onClick={onToggle}>
          {open ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}
        </Button>
      ) : (
        <span className={cn(menuLook ? 'w-5' : 'w-6', 'shrink-0')} aria-hidden />
      )}
      <Button
        variant="ghost"
        size="sm"
        className={
          menuLook
            ? cn('h-8 min-w-0 flex-1 justify-start gap-2 px-1.5 text-sm font-normal', flat, active ? 'text-primary font-medium' : 'text-muted-foreground hover:text-foreground')
            : cn('h-7 min-w-0 flex-1 justify-start gap-2 px-1.5 font-normal', active ? 'text-primary font-medium' : 'text-foreground')
        }
        aria-current={active ? 'true' : undefined}
        onClick={onSelect}
        // Long names are cut short; the full name is here, on hover.
        title={title ?? label}
      >
        <span className={cn('shrink-0', menuLook && active ? 'text-primary' : 'text-muted-foreground')}>{icon}</span>
        <span className="min-w-0 truncate">{label}</span>
        {hint && <span className="text-muted-foreground ml-auto shrink-0 text-xs">{hint}</span>}
      </Button>
      {menu.length > 0 && (
        <DropdownMenu
          label={`${label} — folder actions`}
          items={menu}
          align="end"
          trigger={({ open: _open, ...triggerProps }) => (
            <Button
              {...triggerProps}
              variant="ghost"
              size="sm"
              className={cn(menuLook ? 'h-8 w-6' : 'h-7 w-7', 'shrink-0 px-0 opacity-70 group-hover:opacity-100', flat)}
              aria-label={`${label} — folder actions`}
            >
              <Ellipsis size={14} aria-hidden />
            </Button>
          )}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------ breadcrumb */

/**
 * Where the register stands, as a way back up: the workspace, then each folder
 * from the root down (`GET /folders/{id}`'s breadcrumb). Without a folder it is
 * the workspace alone; without a workspace, nothing.
 */
export function FolderBreadcrumb({
  client,
  place,
  workspaceName,
  onSelect,
  trailing,
  revision = 0,
}: {
  client: ProDocClient
  place: FolderPlace
  workspaceName: string | null
  onSelect: (place: FolderPlace) => void
  /** After the last step — the host's *include subfolders* switch. */
  trailing?: ReactNode
  /** Bump to read the steps again — after a rename or a move changed a name or a place on the way. */
  revision?: number
}) {
  const detail = useLoad(() => (place.folder ? client.folders.get(place.folder) : Promise.resolve(null)), [client, place.folder, revision])
  if (!place.workspace) return null
  const crumbs: FolderCrumb[] = place.folder ? (detail.data?.breadcrumb ?? []) : []
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-1 text-sm">
      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-wrap items-center gap-x-1">
        <Crumb label={workspaceName ?? 'Workspace'} current={!place.folder} onClick={() => onSelect({ workspace: place.workspace, folder: null })} />
        {place.folder && detail.loading && !detail.data && (
          <>
            <ChevronRight size={13} className="text-muted-foreground" aria-hidden />
            <Skeleton className="h-4 w-24" />
          </>
        )}
        {place.folder && detail.error && !detail.data && (
          <>
            <ChevronRight size={13} className="text-muted-foreground" aria-hidden />
            <span className="text-muted-foreground text-xs">This folder could not be read — it may have been archived or moved out of sight.</span>
          </>
        )}
        {crumbs.map((c, i) => (
          <span key={c.id} className="flex items-center gap-x-1">
            <ChevronRight size={13} className="text-muted-foreground" aria-hidden />
            <Crumb label={c.name} current={i === crumbs.length - 1} onClick={() => onSelect({ workspace: place.workspace, folder: c.id })} />
          </span>
        ))}
      </nav>
      {trailing}
    </div>
  )
}

function Crumb({ label, current, onClick }: { label: string; current: boolean; onClick: () => void }) {
  if (current) {
    return (
      <span className="text-foreground max-w-56 truncate font-medium" aria-current="location" title={label}>
        {label}
      </span>
    )
  }
  return (
    <Button variant="link" size="sm" className="h-auto max-w-48 truncate px-0" onClick={onClick} title={label}>
      {label}
    </Button>
  )
}

/* ----------------------------------------------------------------- order */

/** A workspace's whole tree (`?tree=true`), flat, in reading order — each folder after its parent, siblings by name. */
export function treeOrder(folders: Folder[]): Folder[] {
  const live = folders.filter((f) => f.status !== 'archived')
  const children = new Map<string, Folder[]>()
  for (const f of live) children.set(f.parent_id ?? '', [...(children.get(f.parent_id ?? '') ?? []), f])
  for (const list of children.values()) list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }))
  const out: Folder[] = []
  const walk = (parent: string, seen: Set<string>) => {
    for (const f of children.get(parent) ?? []) {
      if (seen.has(f.id)) continue
      seen.add(f.id)
      out.push(f)
      walk(f.id, seen)
    }
  }
  walk('', new Set())
  return out
}

/** The folder and every folder below it, by id — what a move may not land in (`cycle`). */
export function subtreeOf(folders: Folder[], rootId: string): Set<string> {
  const ids = new Set([rootId])
  let grew = true
  while (grew) {
    grew = false
    for (const f of folders) {
      if (f.parent_id && ids.has(f.parent_id) && !ids.has(f.id)) {
        ids.add(f.id)
        grew = true
      }
    }
  }
  return ids
}

/** A workspace's folders, whole, for the pickers — and the same list in reading order. */
export function useFolderList(client: ProDocClient, workspaceId: string | null) {
  const loaded = useLoad(() => (workspaceId ? client.folders.tree(workspaceId) : Promise.resolve([] as Folder[])), [client, workspaceId])
  const ordered = useMemo(() => treeOrder(loaded.data ?? []), [loaded.data])
  return { ...loaded, ordered }
}
